import { KebabToCamelCase, KebabToPascalCase } from './string.types';

/**
 * Converts a string from kebab case to camel case.
 *
 * @param text The string.
 */
export function kebabToCamelCase<TString extends string>(
  text: TString,
): KebabToCamelCase<TString> {
  return text.replace(/-\w/g, clearAndUpper) as KebabToCamelCase<TString>;
}

/**
 * Converts a string from kebab case to pascal case.
 *
 * @param text The text.
 */
export function kebabToPascalCase<TString extends string>(
  text: TString,
): KebabToPascalCase<TString> {
  return text.replace(
    /(^\w|-\w)/g,
    clearAndUpper,
  ) as KebabToPascalCase<TString>;
}

/**
 * Converts a string to a hexadecimal color.
 *
 * @example
 *   toHexColor('foo'); // #018CC6
 *
 * @param text The text.
 */
export function toHexColor(text: string): string {
  const hashCode = toHashCode(text);
  const color = (hashCode & 0x00ffffff).toString(16).toUpperCase();
  return `#${'00000'.substring(0, 6 - color.length) + color}`;
}

/**
 * Converts a string to a boolean.
 *
 * @param text The text.
 */
export function toBoolean(text: string): boolean {
  if (!['true', 'false'].includes(text)) {
    throw Error(`The '${text}' cannot be converted to a boolean.`);
  }

  return text === 'true';
}

/**
 * Removes the dashes and upper cases the text.
 *
 * @param text The text.
 */
function clearAndUpper(text: string) {
  return text.replace(/-/, '').toUpperCase();
}

/**
 * Gets the hash code of a string.
 *
 * @example
 *   toHashCode('foo'); // 101574
 *
 * @param text The text.
 */
function toHashCode(text: string): number {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

/**
 * Converts a list of strings to Associated Press Style comma string.
 *
 * @param list List of strings to format
 */
export function listToAPStyleComma(list: readonly string[]): string {
  const listCopy = [...list];
  if (listCopy.length === 1) {
    return listCopy[0];
  }

  if (listCopy.length > 1) {
    const lastItem = listCopy[listCopy.length - 1];
    listCopy.pop(); // Remove the last entry
    const commaSeparatedListItems = listCopy.join(', ');

    return `${commaSeparatedListItems} and ${lastItem}`;
  }

  return '';
}

/**
 * Converts a string from camel to sentence case.
 *
 * @param text The string to convert.
 */
export function camelToSentenceCase(text: string): string {
  const result = text.replace(/([A-Z])/g, ' $1').toLowerCase();

  return `${result.charAt(0).toUpperCase()}${result.slice(1)}`;
}
